import { useCallback, useMemo, useState } from 'react'
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import clsx from 'clsx'
import { UIMedSummaryEntry } from '../store/schemas'
import { selectUIMedSummaryEntriesList } from '../store/entries.selectors'
import ReferencedCell from './ReferencedCell'
import MedSummarySourceFileCell from './MedSummarySourceFileCell'
import CommentCell from './CommentCell'
import BillingEntryTypePill from './BillingEntryTypePill'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ErrorComponent from '@/components/error/error-component'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { MedSummaryParentProvider, type MedSummaryParentContextValue } from '@/medical-billing-summaries/parent-context'
import { MedSummaryAssetActions } from '@/medical-billing-summaries/asset/store/slice'
import { selectDataErrorForAsset } from '@/medical-billing-summaries/asset/store/selectors'
import { PopoverContainerContext } from '@/context/popover-container-context'
import { useTableCellScroll } from '@/medical-common/hooks/use-table-cell-scroll'
import type { CellPosition } from '@/medical-common/hooks/use-table-cell-scroll'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import { CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS } from '@/medical-common/constants'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { EditableEntryField } from '../editable-field-config'

// Row height for virtualization (in pixels) - med summary entries table rows are fixed
const ROW_HEIGHT_PX = 42

/**
 * Helper to get column width styles
 */
const getColumnWidthProps = (size: number, isWidthFlexible?: boolean) => {
  if (isWidthFlexible) {
    // For columns with flexible width, only set minWidth to allow horizontal growth
    return {
      minWidth: `${size}px`,
    }
  }
  // For fixed columns, lock to exact size
  return {
    width: `${size}px`,
    minWidth: `${size}px`,
    maxWidth: `${size}px`,
  }
}

interface MedSummaryEntriesTableProps {
  parentId: string
}

/**
 * Medical Billing Summary Entries Table
 *
 * Renders a virtualized table of the living asset's billing summary entries (the asset store's
 * refresh listener owns loading). Provides the parent context that cells use to key store reads
 * and route entry edits.
 */
export default function MedSummaryEntriesTable({ parentId }: MedSummaryEntriesTableProps) {
  const dispatch = useAppDispatch()
  const { toggleWidget } = useHubspotConversations()

  const parentContext: MedSummaryParentContextValue = useMemo(() => ({ id: parentId }), [parentId])

  // Redux state
  const entriesList = useAppSelector((state: RootState) => selectUIMedSummaryEntriesList(state, { parentId }))
  const isAnyFieldEditing = useAppSelector((state: RootState) => state.medSummaryEntryEditState.activeEdit !== null)
  const assetDataError = useAppSelector((state: RootState) => selectDataErrorForAsset(state, { assetId: parentId }))

  // Scroll container for the virtualizer and popover context
  const [scrollableContainerElement, setScrollableContainerElement] = useState<HTMLDivElement | null>(null)

  // Determine layout: desktop shows content viewer as side column, mobile shows as slideover modal
  const { width: viewportWidth } = useViewportSizeInPixels()
  const isColumnLayout = viewportWidth >= DEVICE_TYPE_BREAKPOINTS.DESKTOP

  // Set up auto-scroll: when a cell is clicked, scroll it into view if using column layout
  // This prevents the content viewer column from covering the clicked cell
  const scrollToCell = useTableCellScroll(scrollableContainerElement, {
    rows: entriesList ?? [],
    delayMs: CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS,
  })
  const handleScroll = useCallback(
    (position: CellPosition) => {
      // Only scroll in column layout (desktop). In slideover layout (mobile), no scroll needed
      // since the content viewer takes over the full screen
      if (!isColumnLayout) return
      scrollToCell(position)
    },
    [isColumnLayout, scrollToCell]
  )

  const columnHelper = useMemo(() => createColumnHelper<UIMedSummaryEntry>(), [])

  const columns = useMemo(() => {
    const allColumns = [
      columnHelper.display({
        id: 'rowNumber',
        // Show human-friendly row index
        cell: ({ row }) => <div className="p-1 pt-2 text-xs text-brand-neutral-600 flex justify-center">{row.index + 1}</div>,
        size: 35,
      }),

      columnHelper.accessor('serviceDate', {
        header: 'Date',
        cell: ({ getValue, row, cell }) => (
          <ReferencedCell
            item={getValue()}
            rowId={row.id}
            cellId={cell.id}
            scrollToCell={handleScroll}
            entryId={row.original.id}
            fieldKey={EditableEntryField.SERVICE_DATE}
          />
        ),
        size: 134,
        meta: { hoverable: true },
      }),

      columnHelper.accessor('facilityName', {
        header: 'Facility',
        cell: ({ getValue, row, cell }) => (
          <ReferencedCell
            item={getValue()}
            rowId={row.id}
            cellId={cell.id}
            scrollToCell={handleScroll}
            entryId={row.original.id}
            fieldKey={EditableEntryField.FACILITY_NAME}
          />
        ),
        size: 274,
        meta: { hoverable: true },
      }),

      columnHelper.accessor('entryType', {
        header: 'Type',
        cell: ({ getValue }) => (
          <div className="p-2">
            <BillingEntryTypePill entryType={getValue()} />
          </div>
        ),
        size: 100,
      }),

      columnHelper.accessor('amount', {
        header: 'Amount',
        cell: ({ getValue, row, cell }) => (
          <ReferencedCell
            item={getValue()}
            rowId={row.id}
            cellId={cell.id}
            scrollToCell={handleScroll}
            entryId={row.original.id}
            fieldKey={EditableEntryField.AMOUNT}
          />
        ),
        size: 126,
        meta: { hoverable: true },
      }),

      columnHelper.accessor('procedureCode', {
        header: 'Procedure Code',
        cell: ({ getValue, row, cell }) => (
          <ReferencedCell
            item={getValue()}
            rowId={row.id}
            cellId={cell.id}
            scrollToCell={handleScroll}
            entryId={row.original.id}
            fieldKey={EditableEntryField.PROCEDURE_CODE}
          />
        ),
        size: 140,
        meta: { hoverable: true },
      }),

      columnHelper.accessor('filePath', {
        header: 'Source File',
        cell: ({ row, cell }) => (
          <MedSummarySourceFileCell source={row.original.source} pages={row.original.pages} rowId={row.id} cellId={cell.id} scrollToCell={handleScroll} />
        ),
        size: 395,
        meta: { isWidthFlexible: true },
      }),

      columnHelper.accessor('comment', {
        header: 'Comments',
        cell: ({ getValue, row }) => <CommentCell comment={getValue()} entryId={row.original.id} />,
        size: 560,
      }),
    ]

    return allColumns
  }, [columnHelper, handleScroll])

  const table = useReactTable({
    data: entriesList ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    getRowId: (row) => row.id,
  })

  // Virtualization config
  const { rows } = table.getRowModel()
  const rowVirtualized = useMemo(
    () => ({
      count: rows.length,
      getScrollElement: () => scrollableContainerElement,
      estimateSize: () => ROW_HEIGHT_PX,
      overscan: 20, // Render extra rows to minimize white gaps during fast scrolls
      getItemKey: (index: number) => rows[index]?.id,
    }),
    [rows, scrollableContainerElement]
  )
  const rowVirtualizer = useVirtualizer(rowVirtualized)

  // The virtualized window
  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  // Spacers simulate the height of unmounted rows above and below the window
  const padTop = virtualItems.length ? virtualItems[0].start : 0
  const padBot = virtualItems.length ? totalSize - virtualItems[virtualItems.length - 1].end : 0

  // Display loading indicator while the asset store loads entries
  if (entriesList === null && !assetDataError) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <CircularProgressContinuousSized size={20} thickness={7} />
      </div>
    )
  }

  // Display error if data fails to load
  if (assetDataError || entriesList === null) {
    return (
      <ErrorComponent
        code="500"
        title="Error Loading Medical Billing Summary"
        message={
          <>
            {'Our servers ran into a problem. Please '}
            <button
              onClick={() => dispatch(MedSummaryAssetActions.refreshAssetData({ assetId: parentId }))}
              className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
            >
              try again
            </button>
            {', or '}
            <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
              contact support
            </button>
            {' if the issue continues.'}
          </>
        }
      />
    )
  }

  if (entriesList.length === 0) {
    return (
      <div className="p-24 flex items-center justify-center">
        <p className="text-brand-neutral-600">No entries to display</p>
      </div>
    )
  }

  return (
    <MedSummaryParentProvider value={parentContext}>
      <PopoverContainerContext.Provider value={scrollableContainerElement}>
        <div className="flex-1 flex flex-col min-h-0 relative will-change-transform">
        {/* Overlay between the rounded sticky header and scrolling body hides the left and right borders that peek through as the rows scroll up */}
        <div className="absolute top-0 left-2 right-2 lg:left-4 lg:right-4 h-8 bg-brand-neutral-50 z-30 pointer-events-none" />

        {/* Scrollable container */}
        <div
          ref={setScrollableContainerElement}
          className={clsx(
            'flex-1 min-h-0 mt-2 lg:mt-4 overscroll-none',
            // Left scroll padding leaves room for the edit field popover when entering edit mode
            'scroll-pl-0.5',
            isAnyFieldEditing ? 'overflow-hidden' : 'overflow-auto'
          )}
          style={{
            // Top scroll padding accounts for the sticky header height (use inline styling so we can use the constant value)
            scrollPaddingTop: `${ROW_HEIGHT_PX}px`,
          }}
        >
          {/* Padding goes here instead of the scroll container because Safari can collapse right padding on overflow-auto */}
          <div className={clsx('inline-block min-w-full px-2 pb-2 lg:px-4 lg:pb-4', isAnyFieldEditing && 'pointer-events-none')}>
            {/* Table */}
            <table className="border-separate border-spacing-0">
              {/* Sticky header */}
              <thead className="bg-brand-neutral-50 sticky top-0 z-40">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isFirstCell = header.index === 0
                      const isLastCell = header.index === headerGroup.headers.length - 1
                      return (
                        <th
                          key={header.id}
                          className={clsx(
                            'text-left text-sm border-r border-b border-t font-medium px-4 py-2.5',
                            isFirstCell && 'rounded-tl-md border-l',
                            isLastCell && 'rounded-tr-md'
                          )}
                          // Set the header cell width to the column's computed size
                          // Without this, the header and body can size themselves differently and the columns won't align
                          style={getColumnWidthProps(header.getSize(), header.column.columnDef.meta?.isWidthFlexible)}
                        >
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>

              {/* Table body */}
              <tbody>
                {/* Top spacer to represent offscreen rows above - spacer rows represent the height of all the unmounted rows above/below the
                    visible window. Required for virtualization so the scroll height behaves as if the full table is rendered. */}
                {padTop > 0 && (
                  <tr style={{ height: padTop }} aria-hidden>
                    <td colSpan={columns.length} />
                  </tr>
                )}

                {/* Only the visible rows are actually mounted */}
                {virtualItems.map((vi) => {
                  const row = rows[vi.index]
                  const isLastRow = vi.index === rows.length - 1
                  return (
                    <tr key={row.id} data-index={vi.index}>
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const isFirstCell = cellIndex === 0
                        const isLastCell = cellIndex === row.getVisibleCells().length - 1
                        return (
                          <td
                            key={cell.id}
                            id={cell.id}
                            className={clsx(
                              'bg-white text-sm border-r border-b p-0',
                              isFirstCell && 'border-l',
                              isLastRow && isFirstCell && 'rounded-bl-md',
                              isLastRow && isLastCell && 'rounded-br-md',
                              cell.column.columnDef.meta?.hoverable && 'relative'
                            )}
                            // Apply the same fixed width as the header.
                            // Prevents column shifts or misalignment caused by virtualization only rendering some rows.
                            style={getColumnWidthProps(cell.column.getSize(), cell.column.columnDef.meta?.isWidthFlexible)}
                          >
                            <div className="h-full w-full">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}

                {/* Bottom spacer to represent offscreen rows below */}
                {padBot > 0 && (
                  <tr style={{ height: padBot }} aria-hidden>
                    <td colSpan={columns.length} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </PopoverContainerContext.Provider>
    </MedSummaryParentProvider>
  )
}
